import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FIELD_HEIGHT } from '../../contracts/field';
import { FieldSelectMenuOption, FieldSelectMenuOptionLabelWrapper, FieldSelectOptionsWrapper } from '../fields/select-styled-components';
import { Popover } from '../popover/index';
import { TextField } from '../text-field/index';

const SELECT_ITEM_OPTION_HEIGHT = 35;

const SelectItemOption = styled.div<{ $height: number; $selected: boolean; $leftItemWidth?: number; $paddingRight?: number }>`
	display: grid;
	grid-template-columns: ${props => (props.$leftItemWidth ? `${props.$leftItemWidth}px 1fr` : '1fr')};
	align-items: center;
	padding: 0px 20px;
	height: ${props => `${props.$height}px`};
	transition: all 0.5s ease-in-out;
	position: relative;
	cursor: pointer;
	background-color: ${props => (props.$selected ? 'rgba(30, 18, 111, 0.08)' : 'inherit')};
	&:hover {
		opacity: 0.5;
	}
	${props => (props.$paddingRight ? `padding-right: ${props.$paddingRight}px` : '')}
`;

function FilterableSelectOverlay<T>(props: SelectPopoverContentProps<T>) {
	const [search, setSearch] = useState<string>('');
	const filteredOptions = props.items?.filter(o => props.labelSelector(o)?.toString()?.toLowerCase()?.includes(search?.toLowerCase()));

	const didMountRef = useRef(true);
	useEffect(() => {
		didMountRef.current = true;
		return () => void (didMountRef.current = false);
	}, []);
	const focusElement = useCallback(inputElement => {
		if (inputElement) {
			// Delay the focus to avoid the popover jump shift
			setTimeout(() => {
				// If the component has unmounted, don't focus
				if (!didMountRef.current) return;
				inputElement.focus();
			}, 200);
		}
	}, []);
	return (
		<div style={{ display: 'flex', flexDirection: 'column' }}>
			<div style={{ paddingLeft: '1.5rem', paddingTop: '1rem', paddingRight: '1.5rem' }}>
				<TextField
					style={{ width: '100%' }}
					value={search}
					onChange={v => setSearch(v.target.value)}
					placeholder={'Search available options'}
					//variant="standard"
					type={'search'}
					inputProps={{ 'aria-autocomplete': 'none', autocomplete: 'off' }}
					inputRef={focusElement}
				/>
			</div>
			<div style={{ minWidth: '17rem', maxHeight: 450, overflowY: 'auto' }}>
				<div style={{ display: 'flex', flexDirection: 'column' }}>
					{filteredOptions?.map(o => {
						const key = props.keySelector(o);
						const label = props.labelSelector(o);
						const backGroundColor = props.bgColourSelector(o);
						return (
							<SelectItemOption
								$height={SELECT_ITEM_OPTION_HEIGHT}
								$selected={props.isItemSelected(o)}
								$leftItemWidth={backGroundColor !== 'transparent' ? 25 : undefined}
								key={key}
								onClick={() => {
									props.onItemSelected(o);
								}}
							>
								{backGroundColor !== 'transparent' && <ItemColor color={backGroundColor} />}
								<FieldSelectMenuOptionLabelWrapper title={label}>{label}</FieldSelectMenuOptionLabelWrapper>
							</SelectItemOption>
						);
					})}
				</div>
			</div>
		</div>
	);
}

export const ItemColor: React.FC<{ color: string }> = ({ color }) => (
	<div
		style={{
			marginRight: 7,
			cursor: 'pointer',
			width: '20px',
			height: '20px',
			borderRadius: 10,
			border: `1px solid ${color}`,
			backgroundColor: color
		}}
	/>
);

type SelectPopoverContentProps<T> = {
	visible: boolean;
	onVisibleChange: (vis: boolean) => void;
	items?: T[];
	fitPopoverWidthToChild: boolean;
	labelSelector: (item: T) => string;
	keySelector: (item: T) => string;
	isItemSelected: (item: T) => boolean;
	bgColourSelector: (item: T) => string | 'transparent';
	onItemSelected: (item: T) => void;
};

const ITEM_CUTOFF = 10;

export function SelectPopoverContent<T>(props: SelectPopoverContentProps<T> & { children: React.ReactElement }) {
	return (
		<Popover
			visible={props.visible}
			trigger="click"
			fitWidthToChild={props.fitPopoverWidthToChild}
			onVisibleChange={v => {
				if (v !== props.visible) props.onVisibleChange(v);
			}}
			overlay={() => {
				if ((props.items ?? []).length > ITEM_CUTOFF) {
					return <FilterableSelectOverlay {...props} />;
				} else {
					return (
						<FieldSelectOptionsWrapper $optionCount={props.items?.length ?? 1}>
							{props.items?.map(o => {
								const key = props.keySelector(o);
								const label = props.labelSelector(o);
								const backGroundColor = props.bgColourSelector(o);

								return (
									<FieldSelectMenuOption
										$height={FIELD_HEIGHT}
										$selected={props.isItemSelected(o)}
										$leftItemWidth={backGroundColor !== 'transparent' ? 25 : undefined}
										key={key}
										onClick={() => {
											props.onItemSelected(o);
										}}
									>
										{backGroundColor !== 'transparent' && <ItemColor color={backGroundColor} />}
										<FieldSelectMenuOptionLabelWrapper title={label}>{label}</FieldSelectMenuOptionLabelWrapper>
									</FieldSelectMenuOption>
								);
							})}
						</FieldSelectOptionsWrapper>
					);
				}
			}}
		>
			{props.children}
		</Popover>
	);
}
